"use client";

import { useCallback, useMemo } from "react";
import { useTranslations } from "@zapier/i18n";
import {
  type HydratedCanvas,
  useCanvasActionPermissions,
  useSplitTreatments,
  useSupportedAssets,
} from "@zapier/client";
import {
  KebabMenuItem,
  useEmitCanvasKebabInteractionEvent,
} from "@zapier/shared";
import { CanvasModal } from "./CanvasModals";

interface UseCanvasRowInput {
  canvas: HydratedCanvas;
  setCurrentCanvas: (canvas: HydratedCanvas | undefined) => void;
  setCurrentModal: (modal: CanvasModal | undefined) => void;
  onCopyToAnotherAccount?: (asset: HydratedCanvas) => void;
  isTrashed?: boolean;
}

export function useCanvasRow({
  canvas,
  setCurrentCanvas,
  setCurrentModal,
  onCopyToAnotherAccount,
  isTrashed,
}: UseCanvasRowInput) {
  const t = useTranslations("canvases-table");
  const tAction = useTranslations("buttons");
  const permissions = useCanvasActionPermissions(canvas.id);
  const { isMPFEnabled } = useSupportedAssets();
  const { isZapAndCanvasUnifiedTrashEnabled } = useSplitTreatments();
  const { emit } = useEmitCanvasKebabInteractionEvent();

  const isPermanentDelete = isTrashed || !isZapAndCanvasUnifiedTrashEnabled;

  const handleDeleteCanvas = useCallback(() => {
    setCurrentCanvas(canvas);
    setCurrentModal(CanvasModal.Delete);
    emit("delete", canvas.id);
  }, [setCurrentCanvas, canvas, setCurrentModal, emit]);

  const handleMoveCanvas = useCallback(() => {
    setCurrentCanvas(canvas);
    setCurrentModal(CanvasModal.Move);
    emit("move", canvas.id);
  }, [setCurrentCanvas, canvas, setCurrentModal, emit]);

  const handleShareCanvas = useCallback(() => {
    setCurrentCanvas(canvas);
    setCurrentModal(CanvasModal.Share);
    emit("share", canvas.id);
  }, [setCurrentCanvas, canvas, setCurrentModal, emit]);

  const handleRenameCanvas = useCallback(() => {
    setCurrentCanvas(canvas);
    setCurrentModal(CanvasModal.Rename);
    emit("rename", canvas.id);
  }, [setCurrentCanvas, canvas, setCurrentModal, emit]);

  const handleChangeOwner = useCallback(() => {
    setCurrentCanvas(canvas);
    setCurrentModal(CanvasModal.ChangeOwner);
    emit("change_owner", canvas.id);
  }, [setCurrentCanvas, canvas, setCurrentModal, emit]);

  const disabledShareMessage = permissions.hasTeams
    ? undefined
    : t("permissions.no-teams-feature");

  const changeOwnerTooltipText = permissions.isLoading
    ? undefined
    : permissions.hasTeams
      ? t("permissions.no-change-owner-permission")
      : t("permissions.no-teams-feature");

  const menuItems = useMemo(
    () =>
      [
        {
          disabled: !permissions.canRename,
          icon: "actionEdit",
          label: t("actions.rename"),
          onClick: handleRenameCanvas,
        },
        {
          label: "Share canvas",
          icon: "personAdd",
          onClick: handleShareCanvas,
          disabled: !!disabledShareMessage,
          tooltipText: disabledShareMessage,
        },
        isMPFEnabled && {
          disabled: !permissions.canMove,
          icon: "actionMoveToFolder",
          label: t("actions.move-to-folder"),
          onClick: handleMoveCanvas,
        },
        {
          label: t("actions.change-owner"),
          onClick: handleChangeOwner,
          icon: "arrowConnect",
          disabled: !permissions.canChangeOwner,
          tooltipText: changeOwnerTooltipText,
        },
        { divider: true, label: "divider" },
        onCopyToAnotherAccount && {
          icon: "arrowExit",
          label: tAction("copy-to-another-account"),
          onClick: () => {
            onCopyToAnotherAccount(canvas);
          },
        },
        onCopyToAnotherAccount && {
          divider: true,
          label: "divider",
        },
        {
          disabled: !permissions.canDelete,
          hasWarning: true,
          icon: "actionTrash",
          label: t("actions.delete", {
            isPermanentDelete: String(isPermanentDelete),
          }),
          onClick: handleDeleteCanvas,
          tooltipText: permissions.isLoading
            ? undefined
            : t("permissions.no-delete-permission"),
        },
      ].filter(Boolean) as KebabMenuItem[],
    [
      permissions.canRename,
      permissions.canMove,
      permissions.canChangeOwner,
      permissions.canDelete,
      permissions.isLoading,
      isPermanentDelete,
      t,
      handleRenameCanvas,
      handleShareCanvas,
      disabledShareMessage,
      isMPFEnabled,
      onCopyToAnotherAccount,
      tAction,
      canvas,
      handleMoveCanvas,
      changeOwnerTooltipText,
      handleChangeOwner,
      handleDeleteCanvas,
    ],
  );

  return {
    disabledShareMessage,
    handleShareCanvas,
    menuItems,
    permissions,
  };
}
